// A partner's request to book an experience for one of their guests.
//
// Created by the inquiry form on the public experience page (see
// `InquiryFormCard`) and worked through by Eighty Days in the CMS. Both halves
// go through Cloud Functions, and for different reasons:
//
//   - creating one goes through `createBookingRequest`, so the partner it is
//     attributed to, the experience it names and the 'pending' status it starts
//     in are the server's word rather than the browser's — and so the
//     notification email is sent somewhere a client cannot suppress;
//   - moving one between states goes through `setBookingRequestStatus`, because
//     a transition is no longer a status flip. It mints confirmation tokens,
//     tells /portal, computes cancellation fees and sends mail, none of which a
//     browser can be trusted with and several of which must happen inside a
//     transaction.
//
// Requests are never deleted. A request we could not fill is the record of a
// guest we could not accommodate, which is worth more than a tidy collection.

import { closureMessageFor, type ExperienceClosuresType } from './types_closure';
import {
  isCalendarBookingType,
  isNonRefundableBookingType,
  resolveOptionalServices,
  type ExperienceBookingType,
  type ExperienceOptionalServiceType,
  type ExperienceType,
} from './types_experience';
import type { HistoryInfoType } from './types_history';

// Firestore collection name.
export const coll_bookingRequests = 'bookingRequests';

/**
 * Where the desk's private notes live, one document per request and keyed by the
 * request's own id.
 *
 * A second collection rather than a field, because a partner may read its own
 * booking requests straight out of Firestore — see the rules. A field named
 * `internalNotes` on that document would be hidden by our screens and readable
 * by anyone at the partner who opened a console, which is the one thing a note
 * about a partner must not be. The rules on this collection allow admins only, so
 * "internal" is enforced by the database rather than promised by the UI.
 */
export const coll_bookingRequestNotes = 'bookingRequestNotes';

// --- Status ---

/**
 * Where a request has got to. Seven states, and the distinctions between the
 * four ending ones are the point rather than pedantry:
 *
 *   - 'withdrawn' is the partner changing its mind, 'expired' is the partner never
 *     answering, and knowing which is which is the difference between a lost
 *     booking and a follow-up worth making;
 *   - 'unavailable' is "we never had it", 'cancelled' is "we had it and lost
 *     it", and only the second can carry a fee or reach /portal.
 */
export type BookingRequestStatusType =
  | 'pending'
  | 'held'
  | 'confirmed'
  | 'cancelled'
  | 'withdrawn'
  | 'expired'
  | 'unavailable';

export const bookingRequestStatusLabels: Record<BookingRequestStatusType, string> = {
  pending: 'Pending',
  held: 'Held',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
  unavailable: 'Unavailable',
};

/**
 * Every move a request may make. Anything not listed is refused by
 * `setBookingRequestStatus`, so this table is the workflow rather than a
 * description of it.
 *
 * Three entries are worth reading twice. 'confirmed' leads only to 'cancelled':
 * a booking that reached /portal cannot quietly become "unavailable", it has to
 * be cancelled properly and released there. 'expired' reopens to 'held' rather
 * than straight to 'confirmed', so re-offering a lapsed hold forces somebody to
 * ring the supplier again first.
 *
 * And 'pending' leads straight to 'confirmed' as well as through 'held', for
 * suppliers who answer yes or no on the phone and cannot hold a date at all.
 * That shortcut is *structurally* legal for anything, but only actually allowed
 * for an 'inquiry-immediate' experience — a table shared with the browser cannot
 * see the experience, so the handler enforces it. Skipping the hold on an
 * ordinary inquiry would commit a partner to a price it was never shown.
 *
 * A direct booking off the calendar enters this table at 'confirmed' rather than
 * travelling to it: the slot *is* the artisan's agreement, so there is nothing to
 * ask and nobody to wait for. Which leaves 'cancelled' as the only move it can
 * make, and that is the whole of its workflow.
 */
export const bookingRequestTransitions: Record<BookingRequestStatusType, readonly BookingRequestStatusType[]> = {
  pending: ['held', 'confirmed', 'unavailable', 'withdrawn'],
  held: ['confirmed', 'withdrawn', 'expired', 'unavailable'],
  confirmed: ['cancelled'],
  expired: ['held'],
  cancelled: [],
  withdrawn: [],
  unavailable: [],
};

export function canTransition(from: BookingRequestStatusType, to: BookingRequestStatusType): boolean {
  return bookingRequestTransitions[from].includes(to);
}

/** Statuses a request can still move on from. Everything else is final. */
export function isTerminalStatus(status: BookingRequestStatusType): boolean {
  return bookingRequestTransitions[status].length === 0;
}

// --- Reasons ---

/**
 * Who ended a confirmed booking, and the only thing the fee is keyed off.
 *
 * The reason beside it is free text — an admin's own words about a particular
 * cancellation, which cannot be reasoned about — so this is what the money hangs
 * on: a supplier pulling out is never the partner's fault, and Eighty Days
 * standing a booking down is not something to charge for either. See
 * `isCancellationChargeable`, and `cancellationFeeWaived` for the case where the
 * partner did end it and the desk decides not to charge anyway.
 */
export type CancellationInitiatorType = 'client' | 'admin' | 'supplier' | 'system';

/**
 * The coded reasons this used to be, kept only to read bookings cancelled before
 * the box became free text. Nothing writes these any more.
 */
const legacyCancellationReasonLabels: Record<string, string> = {
  'supplier-cancelled': 'Supplier cancelled',
  'client-cancelled': 'Partner cancelled',
  'client-no-show': 'Guest did not attend',
  'admin-goodwill': 'Cancelled as a goodwill gesture',
};

/**
 * A stored cancellation reason as a person should read it: the admin's own words,
 * or the label of the code an older booking recorded instead.
 *
 * For Eighty Days' eyes — the CMS and the desk email. What the partner is shown is
 * `cancellationOutcomeLabel`, deliberately not this: the box is where the desk
 * writes what actually happened, and it is not written to be read by the partner
 * it is about.
 */
export function cancellationReasonText(reason: string | undefined): string | undefined {
  if (!reason) return undefined;
  return legacyCancellationReasonLabels[reason] ?? reason;
}

/** How a cancellation is described to the partner. Never the admin's own words. */
export function cancellationOutcomeLabel(
  request: Pick<BookingRequestType, 'cancellationInitiator' | 'cancellationReason'>,
): string {
  const coded = request.cancellationReason && legacyCancellationReasonLabels[request.cancellationReason];
  if (coded) return coded;
  switch (request.cancellationInitiator) {
    case 'supplier':
      return 'Cancelled by the supplier';
    case 'admin':
    case 'system':
      return 'Cancelled by Eighty Days';
    default:
      return 'Cancelled at your request';
  }
}

/** Why a request ended without ever being confirmed. */
export type BookingRequestEndReasonType =
  | 'client-declined-offer'
  | 'client-withdrew'
  | 'no-availability'
  | 'supplier-withdrew';

export const bookingRequestEndReasonLabels: Record<BookingRequestEndReasonType, string> = {
  'client-declined-offer': 'Partner declined the offered date',
  'client-withdrew': 'Partner withdrew the request',
  'no-availability': 'No availability could be found',
  'supplier-withdrew': 'Supplier withdrew during the hold',
};

/** Which end reasons belong with which ending status. Enforced server-side. */
export const bookingRequestEndReasonsByStatus: Record<
  'withdrawn' | 'unavailable',
  readonly BookingRequestEndReasonType[]
> = {
  withdrawn: ['client-declined-offer', 'client-withdrew'],
  unavailable: ['no-availability', 'supplier-withdrew'],
};

/**
 * How a booking came to be confirmed.
 *
 * 'link' and 'portal' are the same answer to the same offer, and they are kept
 * apart because they are not the same act: 'link' is whoever held the emailed
 * token, 'portal' is a named signed-in account at the partner. When a partner
 * queries a booking nobody remembers agreeing to, that distinction is the
 * difference between "a forwarded email was clicked" and "this person clicked
 * it".
 */
export type BookingConfirmedViaType = 'link' | 'phone' | 'direct' | 'portal';

// --- /portal ---

/**
 * Whether /portal knows about a confirmed booking.
 *
 * Tracked apart from `status` on purpose: `status` is what we have promised the
 * partner, and a failed call to /portal must never quietly downgrade a promise we
 * have already made. A booking sitting at 'failed' is ours to reconcile, not
 * theirs to worry about.
 */
export type PortalSyncStateType = 'none' | 'pending' | 'synced' | 'failed';

/**
 * How long a booking reference may be.
 *
 * Generous rather than fitted: /portal's request codes are a fraction of this,
 * and the field is typed by hand until /portal raises the requests itself. A
 * limit is here to stop a paste of half an email landing in a column the queue
 * has to render, not to describe a format we do not own.
 */
export const bookingReferenceMaxLength = 64;

/**
 * A booking reference as somebody typed it: trimmed, and empty where there is
 * nothing to say.
 *
 * Trimmed rather than taken literally because a reference is copied out of
 * another screen, and a trailing space is invisible in the box and fatal to
 * anyone searching for it later. An entry left blank clears the reference
 * instead of storing whitespace — that is how a code entered against the wrong
 * booking is taken off it.
 */
export function cleanBookingReference(value: string | undefined): string {
  return (value ?? '').trim();
}

/**
 * What to call this booking when writing to anyone about it.
 *
 * The request code where one has been minted, and the document id where none
 * has — every email, the recap PDF and the confirmation screen print this, and
 * a booking with no reference at all is worse than one referred to by an ugly
 * id. The two are never confused for each other: a code is eleven characters
 * with a hyphen in the middle, an id is twenty without.
 *
 * One helper rather than the field read directly, so that the day a booking's
 * code changes — or the day /portal starts minting them at creation — every
 * letter already sent and every letter still to send agree about what this
 * booking is called.
 */
export function bookingReference(request: Pick<BookingRequestType, 'id' | 'portalRequestCode'>): string {
  return request.portalRequestCode || request.id;
}

/** What is wrong with a reference, in words for the admin, or null if nothing is. */
export function validateBookingReference(reference: string): string | null {
  if (reference.length > bookingReferenceMaxLength) {
    return `A reference cannot be longer than ${bookingReferenceMaxLength} characters.`;
  }
  // One line, always. This is quoted in emails and read down a column; a
  // pasted newline turns both into something nobody can match against /portal.
  if (/[\r\n\t]/.test(reference)) return 'A reference must be a single line.';
  return null;
}

// --- Cancellation fees ---

/**
 * JST is UTC+9 all year, so a Japanese calendar date's start is exact arithmetic
 * and needs no timezone library. Experiences are quoted in local dates; the
 * functions run in UTC.
 */
export function jstStartOfDay(isoDate: string): string {
  return jstInstant(isoDate, '00:00');
}

/**
 * A Japanese calendar date and an `HH:mm` local time, as an instant.
 *
 * The one place that arithmetic is written, shared with the availability
 * calendar (`slotStartInstant`): a slot's times, a hold deadline and a
 * cancellation window are all quoted in JST and all compared against a clock
 * running in UTC.
 */
export function jstInstant(isoDate: string, time: string): string {
  return `${isoDate}T${time}:00+09:00`;
}

/**
 * The other direction: which Japanese calendar date an instant fell on.
 *
 * The inverse of `jstStartOfDay`, and here beside it because it is the same
 * nine hours of arithmetic read backwards. Invoicing needs it — a cancellation
 * is stamped as an instant but billed in a month, and at half past eight on a
 * Tokyo morning those two disagree about which month.
 */
export function jstDateOf(instant: string): string {
  const d = new Date(instant);
  d.setUTCHours(d.getUTCHours() + 9);
  return d.toISOString().slice(0, 10);
}

/**
 * The last instant of a Japanese calendar date.
 *
 * A second short of midnight rather than the next day's start, so that the day
 * this deadline is quoted as is the day it renders on: every surface shows a
 * free-cancellation deadline as a date, and `2026-09-04T00:00+09:00` reads as
 * the 4th when what was promised was the whole of the 3rd. The lost second is
 * not a policy — a cancellation landing inside it is charged nothing either way,
 * since `isCancellationChargeable` only bites strictly after the deadline.
 */
export function jstEndOfDay(isoDate: string): string {
  return `${isoDate}T23:59:59+09:00`;
}

/**
 * Whether an instant is one of those — the last second of a Japanese day.
 *
 * Which is how a deadline that means "the whole of this day" is written, and
 * therefore how one can be told apart from a deadline that falls at some hour of
 * it. Bookings confirmed under earlier policies carry both kinds, so the
 * question is asked of the instant rather than of the terms beside it: a
 * deadline is quoted to the day only where the day is the whole promise.
 */
export function isJstEndOfDay(instant: string): boolean {
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.toISOString() === new Date(jstEndOfDay(jstDateOf(instant))).toISOString();
}

/** A Japanese calendar date, shifted by whole days. */
export function jstDatePlusDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * The instant a partner stops being able to cancel for free, from the booked date
 * and the experience's window.
 *
 * Snapshotted onto the request when it is confirmed rather than recomputed on
 * demand: editing an experience's policy afterwards must not retroactively
 * change what an existing booking agreed to.
 *
 * The window counts back from midnight at the *start* of the booked day, in
 * whole days: a seven-day policy on the 10th closes at midnight beginning the
 * 3rd, leaving the whole of the 2nd free and the seven days from the 3rd to the
 * 9th chargeable. That is what "seven days before" is agreed with the artisan as
 * — seven clear days of notice — and counting from midnight rather than from the
 * slot means a 10:00 booking and an 18:00 booking on the same date share one
 * deadline: the artisan loses the day either way, so the hour it happens to
 * start at is not what the policy is about.
 *
 * Zero days is the same rule with nothing subtracted — free until midnight
 * beginning the booked day, which is to say for the whole of the day before. It
 * is the most generous window there is rather than a special case, so the hour
 * a slot starts at bears on nothing here.
 *
 * Expressed as the last second of the preceding day rather than as the next
 * day's `00:00`, because a deadline is quoted as a date everywhere it is shown
 * and must read as a day the partner still has — see `jstEndOfDay`.
 */
export function computeFreeCancellationUntil(bookedDate: string, freeCancellationDays: number): string {
  // A negative window is not a longer one: it would push the deadline past the
  // experience itself and make a no-show free.
  const clearDays = Math.max(0, Math.trunc(freeCancellationDays));
  return new Date(jstEndOfDay(jstDatePlusDays(bookedDate, -clearDays - 1))).toISOString();
}

/**
 * The same instant for a booking that is firm the moment it is taken: the
 * confirmation itself.
 *
 * A separate function rather than a number of days, because no number of days
 * expresses this. `freeCancellationDays: 0` means free until midnight beginning
 * the booked day, which is the most generous policy there is — not the
 * strictest. Setting the window to the confirmation instant is what makes every
 * later partner cancellation chargeable, using `isCancellationChargeable`
 * unchanged and leaving supplier cancellations free as they must be.
 *
 * It also means non-refundability needs no field of its own on the request: it
 * is readable back off the same timestamp the fee is judged against, so the two
 * cannot come to disagree. See `isBookingNonRefundable`.
 */
export function nonRefundableCancellationUntil(confirmedAt: string): string {
  return confirmedAt;
}

/**
 * The cancellation policy a request is being worked under, lifted off the
 * experience and frozen onto the request.
 *
 * Frozen at the moment the terms are put to the partner — when a date is offered,
 * or at confirmation where there is no offer step — rather than read live when
 * the answer comes back. A partner accepts the terms it was shown; reclassifying
 * an experience while an offer stands must not turn a refundable date they were
 * offered into a non-refundable booking they never agreed to.
 *
 * The inputs are stored, not the outcome, because the outcome also needs the
 * confirmed date and the confirmation instant, neither of which is known yet
 * when an offer goes out.
 */
export interface BookingCancellationTermsType {
  /** Firm from the instant it is confirmed. See `nonRefundableCancellationUntil`. */
  nonRefundable: boolean;
  /** Clear days of notice before the booked day, when it is not. See `computeFreeCancellationUntil`. */
  freeCancellationDays: number;
}

/**
 * The terms an experience is offering right now.
 *
 * Takes null for a deleted experience and answers with the most generous terms
 * there are, which is the right way to be wrong: it can only ever fail to charge
 * a fee, never invent one we could not justify.
 */
export function cancellationTermsFor(experience: ExperienceType | null): BookingCancellationTermsType {
  if (!experience) return { nonRefundable: false, freeCancellationDays: 0 };
  return {
    nonRefundable: isNonRefundableBookingType(experience.bookingType),
    freeCancellationDays: experience.freeCancellationDays ?? 0,
  };
}

/**
 * What confirming under these terms leaves as the free-cancellation deadline.
 *
 * The one place the two branches meet, so a booking's window is computed the
 * same way whoever confirmed it and whenever the terms were frozen.
 */
export function freeCancellationUntilFor(
  terms: BookingCancellationTermsType,
  { confirmedDate, confirmedAt }: { confirmedDate: string; confirmedAt: string },
): string {
  return terms.nonRefundable
    ? nonRefundableCancellationUntil(confirmedAt)
    : computeFreeCancellationUntil(confirmedDate, terms.freeCancellationDays);
}

// --- Pricing ---

/**
 * One row of what a booking costs.
 *
 * A booking is rarely one number. The experience itself is what the partner asked
 * for, but a date often comes with a transfer, an interpreter, a lunch — things
 * we arrange and pass on at cost, and things the partner does *not* earn its
 * commission on. Charging them means putting them on the invoice; charging
 * commission on them means paying the partner for placing a taxi.
 *
 * So `commissionable` sits on the row rather than on the booking. It is the whole
 * reason the rows exist separately at all: the alternative is one total, one rate
 * and a deduction that quietly overpays on every extra we arrange.
 *
 * Amounts are JPY, tax-inclusive and whole yen, like every other figure that
 * reaches an invoice.
 */
export interface BookingPriceLineType {
  /** What this row is for. The experience's own name, on the first row. */
  label: string;
  /** JPY, tax-inclusive. */
  amount: number;
  /** Whether the partner earns its commission on this row. */
  commissionable: boolean;
}

/**
 * How many rows one booking may be priced by, and how long a row's label may be.
 *
 * Generous caps rather than a judgement about what belongs on a booking —
 * there to bound the document, like the caps on the inquiry form above.
 */
export const bookingPriceMaxLines = 10;
export const bookingPriceLabelMaxLength = 80;

/**
 * The rows actually being charged: labels trimmed, rows nobody filled in dropped.
 *
 * A row is "not filled in" only when it has neither a label nor an amount, which
 * is what an empty row the admin added and did not use looks like. A labelled row
 * of ¥0 is kept — a service we are arranging and not charging for is worth
 * saying on the invoice, and dropping it would silently rewrite the offer.
 *
 * Shared by the form and the Cloud Function, like `cleanPreferredDates`, so the
 * total the admin was shown is the total the server derives.
 */
export function cleanBookingPriceLines(lines: BookingPriceLineType[]): BookingPriceLineType[] {
  return lines
    .map((line) => ({ ...line, label: line.label.trim() }))
    .filter((line) => line.label || line.amount);
}

/** What the booking comes to. The only place the rows are added up. */
export function bookingPriceTotal(lines: BookingPriceLineType[]): number {
  return lines.reduce((sum, line) => sum + line.amount, 0);
}

/**
 * How a booking is priced, including one taken before the rows existed.
 *
 * Everything that displays a breakdown goes through this, so a booking held last
 * month reads as a single row named after its experience rather than as a gap.
 * `agreedPrice` is the total either way — see `BookingRequestType.priceLines` —
 * which is why the fallback can be built from it without arithmetic.
 *
 * The fallback is commissionable because that is what those bookings were
 * invoiced as: before there were rows, the whole price carried the rate.
 */
export function bookingPriceLines(
  request: Pick<BookingRequestType, 'priceLines' | 'agreedPrice' | 'experienceName'>,
): BookingPriceLineType[] {
  if (request.priceLines?.length) return request.priceLines;
  if (typeof request.agreedPrice !== 'number') return [];
  return [{ label: request.experienceName, amount: request.agreedPrice, commissionable: true }];
}

/**
 * Whether a booking is worth itemising, or is just a price.
 *
 * Every surface that quotes a price asks this rather than deciding for itself,
 * so a plain booking reads as one line on the offer email, the offer page and the
 * partner's own list — not as a table with a single row and a total repeating it.
 */
export function hasPriceBreakdown(request: Pick<BookingRequestType, 'priceLines'>): boolean {
  return (request.priceLines?.length ?? 0) > 1;
}

/**
 * Why a set of rows cannot be offered, or null when it can.
 *
 * Shared by the dialog and `setBookingRequestStatus` for the same reason
 * `validateBookingRequest` is: the message an admin sees while typing is the one
 * the server would have sent back. Call it on cleaned rows.
 */
export function validateBookingPriceLines(lines: BookingPriceLineType[]): string | null {
  if (lines.length === 0) return 'Give at least one priced line.';
  if (lines.length > bookingPriceMaxLines) {
    return `A booking can be priced by no more than ${bookingPriceMaxLines} lines.`;
  }
  if (lines.some((line) => !line.label)) return 'Every priced line needs a description.';
  if (lines.some((line) => line.label.length > bookingPriceLabelMaxLength)) {
    return 'One of the descriptions is longer than we can store.';
  }
  if (lines.some((line) => !Number.isInteger(line.amount) || line.amount < 0)) {
    return 'Every price must be a whole number of yen.';
  }
  return null;
}

// --- Optional services ---

/**
 * The services actually asked for: blanks dropped, duplicates collapsed. The
 * same trick as `cleanPreferredDates`, shared with the Cloud Function for the
 * same reason — a form that ticks a box twice is still one shuttle.
 */
export function cleanOptionalServiceIds(ids: string[]): string[] {
  return [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
}

/**
 * The services on a booking, as rows of its price.
 *
 * The one place that conversion is written, because two very different paths
 * make it and they must agree: a direct booking prices itself the instant it is
 * taken, and an inquiry is priced by the desk days later from the same
 * suggestion. Each service carries its own `commissionable`, so a car arranged
 * at cost stays a row the partner earns nothing on however the booking was made.
 */
export function optionalServicePriceLines(
  services: ExperienceOptionalServiceType[],
): BookingPriceLineType[] {
  return services.map((service) => ({
    label: service.label,
    amount: service.price,
    commissionable: service.commissionable,
  }));
}

/** What the ticked services come to. JPY. */
export function optionalServicesTotal(services: ExperienceOptionalServiceType[]): number {
  return bookingPriceTotal(optionalServicePriceLines(services));
}

/**
 * The services as one line each — "Private shuttle (¥15,000)" — for the surfaces
 * that restate a request rather than price it.
 *
 * Takes the formatter because an email and a screen render yen differently and
 * neither should have to know how the other does it, the same arrangement as
 * `offerCancellationLabel`.
 */
export function optionalServiceLines(
  services: ExperienceOptionalServiceType[],
  formatPrice: (amount: number) => string,
): string[] {
  return services.map((service) => `${service.label} (${formatPrice(service.price)})`);
}

// --- Commission ---

/**
 * The partner's cut of a booking, lifted off the experience and frozen onto the
 * request — the same trick as `BookingCancellationTermsType` above, for the same
 * reason and at the same moments.
 *
 * `commissionPercent` is an editable field on the experience, and a booking is
 * invoiced up to six weeks after it is confirmed. Without this, changing an
 * experience's rate in July would silently change what a partner earned on a
 * booking delivered in June — money already agreed, on a booking already
 * delivered, altered by an edit nobody connected to it.
 */
export interface BookingCommissionType {
  enabled: boolean;
  /** Whole percent. Meaningless when `enabled` is false; stored as 0. */
  percent: number;
}

/**
 * The commission an experience is offering right now.
 *
 * A deleted experience answers "none", which is the one place this differs from
 * `cancellationTermsFor` in which way it chooses to be wrong. It cannot be
 * generous here: a commission we have no record of agreeing is not one we can
 * invent, and the alternative — guessing a rate — would put a number on an
 * invoice that nothing supports. Missing a deduction bills a partner too much,
 * which is why it is caught the way it is: every invoice is a draft an admin
 * reads before it is issued.
 */
export function commissionTermsFor(experience: ExperienceType | null): BookingCommissionType {
  if (!experience?.commissionEnabled) return { enabled: false, percent: 0 };
  return { enabled: true, percent: experience.commissionPercent ?? 0 };
}

/**
 * What a booking is actually invoiced at: its own frozen rate, or the live
 * experience for one confirmed before the rate was frozen.
 *
 * The fallback only ever applies to bookings taken before this field existed.
 * Either way the resolved figures are written onto the invoice line, so an
 * invoice once issued cannot move no matter what happens to the experience.
 */
export function resolveBookingCommission(
  request: Pick<BookingRequestType, 'commission'>,
  experience: ExperienceType | null,
): BookingCommissionType {
  return request.commission ?? commissionTermsFor(experience);
}

/**
 * What an offer says about cancellation, before it has been accepted.
 *
 * Resolved server-side and handed to both the email and the offer page, so the
 * letter a concierge reads and the screen they press Confirm on cannot state
 * different terms — which was the whole reason for freezing them.
 *
 * 'unknown' is not a fourth policy: it is an offer made before terms were
 * recorded, where quoting the live experience could state something the offer
 * will not honour. Both surfaces say nothing rather than guess.
 */
export type OfferCancellationType =
  | { state: 'unknown' }
  | { state: 'none' }
  | { state: 'until'; freeUntil: string };

export function offerCancellation(
  offer: Pick<BookingRequestType, 'cancellationTerms' | 'confirmedDate'>,
  now: string,
): OfferCancellationType {
  const terms = offer.cancellationTerms;
  if (!terms || !offer.confirmedDate) return { state: 'unknown' };
  if (terms.nonRefundable) return { state: 'none' };

  const freeUntil = computeFreeCancellationUntil(offer.confirmedDate, terms.freeCancellationDays);
  // A window that has already closed by the time the offer is made is not a
  // window — a date next week under a 14-day policy is firm on acceptance, and
  // quoting a deadline in the past would read as a promise rather than its
  // opposite.
  if (freeUntil <= now) return { state: 'none' };
  return { state: 'until', freeUntil };
}

/**
 * That same answer as one line of prose.
 *
 * Takes the formatter because the two surfaces render an instant differently and
 * neither should have to know how the other does it — but the words themselves
 * are decided here, once.
 */
export function offerCancellationLabel(
  cancellation: OfferCancellationType | undefined,
  formatDay: (iso: string) => string,
): string | null {
  switch (cancellation?.state) {
    case 'until':
      return `Until ${freeCancellationDeadlinePhrase(cancellation, formatDay)}`;
    case 'none':
      return 'Not available — firm once confirmed';
    default:
      return null;
  }
}

/**
 * How a free-cancellation deadline is said out loud: "the end of Wed, 2 Sep
 * 2026" — the last day the partner still has, rather than the midnight that
 * ends it.
 *
 * A phrase rather than a sentence, because the deadline is quoted in both
 * tenses — an offer states one that is running, a cancellation email states one
 * that has run — and the surrounding sentence differs by more than a verb.
 * Decided once all the same, so no surface has to know that the instant it was
 * handed is written as the last second of a day (see `jstEndOfDay`) and reads
 * correctly only when said that way.
 */
export function freeCancellationDeadlinePhrase(
  cancellation: { freeUntil: string },
  formatDay: (iso: string) => string,
): string {
  return `the end of ${formatDay(cancellation.freeUntil)}`;
}

/**
 * The same, for a booking already confirmed — which quotes the deadline stamped
 * on it rather than recomputing one.
 *
 * Takes an instant formatter as well, because not every stamped deadline is a
 * whole day's promise. Bookings confirmed under earlier policies carry windows
 * that ended at the hour a slot began, and one confirmed before terms were
 * recorded at all was computed from whatever the experience said at the time.
 * Those are quoted to the minute rather than rounded up to a day they do not run
 * to — rounding here would promise hours the artisan is already working, and it
 * is the wording a fee is disputed against.
 *
 * Answers null when there is no deadline to quote; a non-refundable booking is
 * the caller's to word, since "no free cancellation" is not a deadline.
 */
export function bookingFreeCancellationPhrase(
  request: Pick<BookingRequestType, 'freeCancellationUntil'>,
  format: { day: (iso: string) => string; instant: (iso: string) => string },
): string | null {
  const freeUntil = request.freeCancellationUntil;
  if (!freeUntil) return null;

  return isJstEndOfDay(freeUntil)
    ? freeCancellationDeadlinePhrase({ freeUntil }, format.day)
    : format.instant(freeUntil);
}

/**
 * The cancellation policy of a confirmed booking, as one sentence.
 *
 * Decided here rather than at each surface, because two of them now state it —
 * the confirmation email to the concierge, and the printed recap attached to it
 * — and a booking whose sheet and covering letter disagree about when it stops
 * being free is worse than one that says nothing.
 *
 * Answers null when there is nothing definite to state, which is a booking
 * confirmed before terms were recorded at all. The caller decides what to do
 * with that: the email falls back to asking the partner to get in touch, the
 * printed sheet leaves the section out rather than printing a shrug.
 */
export function bookingCancellationSentence(
  request: Pick<BookingRequestType, 'freeCancellationUntil' | 'confirmedAt'>,
  format: { day: (iso: string) => string; instant: (iso: string) => string },
): string | null {
  // A non-refundable booking has a window that closed the instant it opened, so
  // quoting it would read as a promise of free cancellation until a time already
  // in the past. Said plainly instead.
  if (isBookingNonRefundable(request)) {
    return 'This booking is confirmed with the artisan from now and cannot be cancelled free of charge.';
  }
  const freeUntil = bookingFreeCancellationPhrase(request, format);
  return freeUntil
    ? `This booking can be cancelled free of charge until ${freeUntil}. After that a cancellation fee may apply.`
    : null;
}

/**
 * Whether a confirmed booking was never free to cancel.
 *
 * Derived rather than stored, and derived from the very field the fee is
 * computed against, so this can only ever agree with what the partner is actually
 * charged. Used for wording — the confirmation email must not promise a free
 * cancellation window that closed the instant it was opened.
 */
export function isBookingNonRefundable(request: Pick<BookingRequestType, 'confirmedAt' | 'freeCancellationUntil'>): boolean {
  if (!request.confirmedAt || !request.freeCancellationUntil) return false;
  return request.freeCancellationUntil <= request.confirmedAt;
}

/**
 * Whether a cancellation is chargeable. The window only ever bites on a partner's
 * own cancellation: a supplier pulling out is never the partner's fault, and a
 * booking Eighty Days stands down is not one to bill for.
 *
 * A no-show is charged by the same rule rather than a rule of its own — the day
 * has arrived by then, so every free-cancellation window there is has already
 * closed, and the artisan has consumed it either way.
 *
 * This says what is *due*. Whether it is actually taken is the desk's to decide
 * afterwards — see `cancellationFeeWaived`.
 */
export function isCancellationChargeable(
  initiator: CancellationInitiatorType,
  at: { now: string; freeCancellationUntil?: string },
): boolean {
  switch (initiator) {
    case 'supplier':
    case 'admin':
    case 'system':
      return false;
    case 'client':
      return !!at.freeCancellationUntil && at.now > at.freeCancellationUntil;
  }
}

// --- The request ---

/**
 * How many dates one request may offer.
 *
 * Each is a real ask on one artisan's diary, so the list is capped rather than
 * unbounded — and a concierge with no preference at all is telling us something
 * other than five dates. The form and the Cloud Function share this number.
 */
export const bookingRequestMaxDates = 5;

// Generous caps, there to bound what a single document can hold rather than to
// second-guess a concierge. Enforced server-side.
export const bookingRequestMaxNameLength = 80;
export const bookingRequestMaxPhoneLength = 40;
export const bookingRequestMaxRequestsLength = 2000;

/** Longest a date may be held for a partner to answer. Enforced server-side. */
export const bookingRequestMaxHoldDays = 30;

/**
 * What the inquiry form collects. The rest of a request — who submitted it,
 * which partner they belong to, the experience's name — is filled in by the
 * Cloud Function from the caller's token and the experience record, so none of
 * it is the browser's to assert.
 */
export interface BookingRequestDraftType {
  experienceId: string;
  customerFirstName: string;
  customerLastName: string;
  contactPhone: string;
  /**
   * `yyyy-mm-dd`, in preference order. The first is the first choice.
   *
   * Empty for a direct booking, which names a slot instead — the server fills
   * this in from the slot it took, so everything downstream (the emails, the
   * admin queue, the invariant that `confirmedDate` is one of the dates on the
   * request) reads the same for both kinds of booking.
   */
  preferredDates: string[];
  /**
   * Which availability slot is being taken, for a 'direct' experience. Ignored
   * for every other booking type, where there is no calendar to take one from.
   */
  slotId?: string;
  guests: number;
  /**
   * Which of the experience's optional services the concierge ticked, by id.
   *
   * Ids rather than the services themselves, for the same reason the partner is
   * not in this payload: what a shuttle costs and whether the partner earns
   * commission on it are the experience's to say, not the browser's. The server
   * resolves them against the live experience and snapshots what it found onto
   * `BookingRequestType.optionalServices`.
   */
  optionalServiceIds: string[];
  specialRequests: string;
}

export interface BookingRequestType extends BookingRequestDraftType {
  id: string;

  // Which partner this is for. `partnerId` is the link; the name is denormalised
  // so the admin list reads without a lookup per row, and so the record still
  // says who asked if the partner is ever renamed.
  partnerId: string;
  partnerName: string;

  // The individual concierge who submitted it, for the same reason.
  requestedByUid: string;
  requestedByEmail: string;
  requestedByName: string;

  experienceName: string;

  /**
   * The optional services asked for, as they stood when the request was made —
   * frozen onto the request like the commission and the cancellation terms, and
   * for the same reason: they are priced here, and repricing a shuttle next
   * month must not change what a booking already taken agreed to.
   *
   * Absent where none were ticked, which is most requests, and absent on
   * requests made before the field existed. The ids that produced it are in
   * `optionalServiceIds`; this is the answer, and the only thing anything
   * downstream reads.
   */
  optionalServices?: ExperienceOptionalServiceType[];

  /**
   * How the experience was to be worked when the request came in, copied here so
   * the admin queue knows whether a pending request can be held or only answered
   * yes/no — it lists requests, not experiences.
   *
   * Only ever a hint for the UI. Every rule that turns on it is re-checked
   * against the live experience inside the transaction, so an experience
   * reclassified since is decided correctly rather than by this copy. Absent on
   * requests created before the field existed; treat that as 'inquiry'.
   */
  experienceBookingType?: ExperienceBookingType;

  status: BookingRequestStatusType;
  createdAt: string;

  // Who last moved it, and when. One set of fields for every transition rather
  // than a pair per status: the detail of *what* changed lives in the fields
  // below, and this only has to answer "who touched it last".
  statusChangedAt?: string;
  statusChangedByUid?: string;
  statusChangedByEmail?: string;

  /** Which of the preferred dates is held or booked. Absent until offered. */
  confirmedDate?: string;

  /**
   * The slot this booking holds, and the times off it, for a direct booking.
   *
   * `slotId` is the link — it is what a cancellation follows to put the date back
   * on the calendar. The times are denormalised beside it because they are read
   * far more often than the slot is: the confirmation email, the admin queue and
   * the partner's own list all state the hour, and none of them should have to
   * fetch a second document (or find it deleted) to do so.
   *
   * All three absent on an inquiry, where nobody has agreed to a time of day —
   * only to a date.
   */
  slotId?: string;
  /** `HH:mm`, JST. */
  confirmedStartTime?: string;
  confirmedEndTime?: string;

  // The offer, while it stands. `heldUntil` is an instant rather than a date:
  // the deadline is quoted to the partner in JST and enforced by a function
  // running in UTC, so a bare date would be ambiguous by nine hours.
  heldUntil?: string;

  /**
   * What the partner is being asked to pay, in total. JPY.
   *
   * Always the sum of `priceLines`, and kept as a field of its own rather than
   * derived on read: it is what a cancellation fee falls back to, what an invoice
   * line is grossed at and what every email quotes, and none of those should have
   * to know how a booking was itemised. The server writes both together — see
   * `bookingPriceTotal`.
   */
  agreedPrice?: number;

  /**
   * The same figure broken out: the experience, and whatever else was arranged
   * with it. Written at the same moments as `agreedPrice` and rewritten by every
   * re-offer, since the price belongs to the offer rather than to the request.
   *
   * Absent on a booking taken before the rows existed, where the single price is
   * read as one commissionable row instead — see `bookingPriceLines`. That
   * fallback is why nothing downstream has to check whether this is set.
   */
  priceLines?: BookingPriceLineType[];

  /**
   * The offer link's secret, hashed. Minted every time the request enters
   * 'held', which is what makes re-offering a lapsed hold invalidate the
   * previous email's link rather than quietly reviving it.
   *
   * Kept after the offer is answered rather than cleared. Verification has to
   * stay possible for the page to tell a concierge who clicks twice that they
   * already answered, instead of that their link is invalid — and the link is
   * read-only by then, disclosing what their own email already did. Only ever a
   * hash; the plaintext exists once, in the email.
   */
  confirmTokenHash?: string;

  // The offer email to the concierge, recorded like `notifiedAt` above and for
  // the same reason: an offer nobody received is one an admin has to chase by
  // phone, and the admin list is where that gets noticed.
  offerSentAt?: string;
  offerEmailError?: string;

  confirmedAt?: string;
  confirmedVia?: BookingConfirmedViaType;

  // What /portal knows. See `PortalSyncStateType` for why this is not folded
  // into `status`.
  portalSyncState?: PortalSyncStateType;
  /**
   * /portal's own code for this booking — the reference the queue shows and the
   * desk quotes when the two systems have to be talked about together.
   *
   * Typed by hand for now, through `setBookingReference`: the requests are
   * raised here and keyed into /portal afterwards, so the code exists over
   * there before anything here can know it. Once /portal raises them itself
   * this becomes something written at creation and rarely touched, which is why
   * it is one field rather than a manual one beside an automatic one — it is
   * the same code either way, and a booking must not end up with two.
   *
   * Deliberately not `portalSyncState`'s business. A code entered here says an
   * admin has matched the two records up; it does not say a machine has, and
   * writing it must not make a booking /portal was never told about look
   * reconciled.
   */
  portalRequestCode?: string;
  portalError?: string;
  portalCancelledAt?: string;

  /**
   * The cancellation policy as it stood when the terms were put to the partner —
   * written when a date is offered, or at confirmation for an experience that
   * has no offer step. Absent until then, and absent on requests held before the
   * field existed, where the live experience is read instead.
   *
   * Unlike `experienceBookingType` above, this one is not a hint: once written it
   * is what the booking is worked under, and the live experience is not consulted
   * again.
   */
  cancellationTerms?: BookingCancellationTermsType;

  /**
   * The partner's commission rate as it stood when this booking was confirmed,
   * frozen for the same reason and at the same moment as the terms above.
   * Absent on bookings confirmed before the field existed, where the live
   * experience is read instead — see `resolveBookingCommission`.
   */
  commission?: BookingCommissionType;

  // Cancellation. `freeCancellationUntil` is computed at confirmation from the
  // terms above, so the fee is judged against the policy the partner agreed to.
  freeCancellationUntil?: string;
  cancelledAt?: string;
  cancellationInitiator?: CancellationInitiatorType;

  /**
   * Why it ended, in the admin's own words. Free text, and Eighty Days' own
   * record rather than the partner's — read it back with `cancellationReasonText`,
   * which also renders the coded reasons older bookings hold here.
   */
  cancellationReason?: string;
  cancellationFee?: number;

  /**
   * Set where a fee was due and the desk chose not to take it — a partner's
   * cancellation the artisan was relaxed about, a booking ended by mutual
   * agreement. `cancellationFee` is then zero, and this is the difference between
   * that and a cancellation that was never chargeable in the first place.
   */
  cancellationFeeWaived?: boolean;

  /** Why a request ended without being confirmed. */
  endReason?: BookingRequestEndReasonType;

  /**
   * A note from the desk that goes to the partner with the offer — "the artisan
   * asks that guests wear socks", "this is the last date before the workshop
   * closes for August". Written when a date is offered, and rewritten by each
   * re-offer, since the note belongs to the offer rather than to the request.
   *
   * On the request document because it is the partner's to read: it appears in the
   * offer email, on the offer link page and in their own request list. Anything
   * *not* for the partner goes in `BookingRequestNotesType`, which lives in another
   * collection for exactly that reason.
   */
  publicNotes?: string;

  /**
   * Which monthly invoice billed the experience itself. Written once as the
   * invoice is issued and never cleared: a booking that changes after its
   * period has been invoiced produces an adjustment on the next invoice rather
   * than altering one already sent.
   */
  invoiceId?: string;

  /**
   * Which invoice carried what the *cancellation* owed — the fee, the credit
   * reversing `invoiceId`'s charge, or both, since they are always raised
   * together.
   *
   * A second field rather than a reuse of the first, because a cancelled
   * booking that had already been billed appears on two invoices and has to
   * remember both. One field could not tell "billed, not yet adjusted" from
   * "adjusted", and the difference is whether the credit goes out again next
   * month.
   */
  adjustmentInvoiceId?: string;

  // Notification bookkeeping, stamped by `createBookingRequest` after it has
  // written the document. A request whose email failed is still a request, so
  // the failure is recorded here instead of aborting the submission — the admin
  // list surfaces it, since nobody may be watching the inbox it never reached.
  notifiedAt?: string;
  notifyError?: string;

  // Present for symmetry with the other content types; nothing writes it yet.
  history?: HistoryInfoType;
}

/**
 * The desk's private note on one request. `id` is the request's id, so the note
 * is found without a query and cannot be orphaned by one.
 *
 * Never sent to the partner, never quoted in an email, and never read by anything
 * but the admin queue — see `coll_bookingRequestNotes` for why it is a document
 * of its own.
 */
export interface BookingRequestNotesType {
  id: string;
  internalNotes: string;
  updatedAt: string;
  updatedByUid: string;
  updatedByEmail: string;
}

/**
 * How long either note may be. Long enough for a paragraph about an artisan's
 * requirements, short enough that a note is not a place to keep documents.
 */
export const bookingNotesMaxLength = 2000;

/** A blank draft for the inquiry form. One empty date row to start. */
export function makeEmptyBookingRequestDraft(experienceId: string, guests: number): BookingRequestDraftType {
  return {
    experienceId,
    customerFirstName: '',
    customerLastName: '',
    contactPhone: '',
    preferredDates: [''],
    guests,
    // Nothing ticked. An optional service is something a concierge asks for, and
    // one selected for them is one the partner is billed for without deciding to.
    optionalServiceIds: [],
    specialRequests: '',
  };
}

/** The dates actually offered: blank rows dropped, duplicates collapsed. */
export function cleanPreferredDates(dates: string[]): string[] {
  return [...new Set(dates.map((d) => d.trim()).filter(Boolean))];
}

const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Why a draft cannot be submitted, or null when it can.
 *
 * Shared by the form and the Cloud Function on purpose: the form uses it to
 * answer without a round trip, the function to actually enforce it. One
 * definition means the two cannot drift into disagreeing about what a valid
 * request is — the message a concierge sees while typing is the same one the
 * server would have sent back.
 *
 * `earliestDate` is passed in rather than read from a clock here, because the
 * two callers do not agree on today: the browser means the concierge's
 * timezone, while the function runs in UTC and allows a day of slack so a
 * same-day request from Tokyo is not rejected as being in the past.
 *
 * `bookingType` decides which of two questions is being answered: a direct
 * booking names one slot off the calendar, everything else offers dates for us to
 * go and ask about. Neither set of rules applies to the other, which is why this
 * takes the type rather than accepting whichever the caller happened to fill in.
 * Whether the named slot is still free is not decided here — that is only
 * knowable inside the booking transaction.
 *
 * `closures` are the days the experience cannot be run on at all. They are
 * checked against the dates offered for an inquiry and not against a direct
 * booking's slot: a slot names a document, and whether *that* still stands is
 * decided in the transaction alongside whether anybody else has taken it.
 *
 * `optionalServices` are the ones this experience currently offers, so a ticked
 * box that no longer names one is refused rather than silently dropped. Passed
 * in rather than read off the draft for the same reason the closures are: the
 * browser's copy of the experience is a copy, and the server's is the truth.
 */
export function validateBookingRequest(
  draft: BookingRequestDraftType,
  limits: {
    minGuests: number;
    maxGuests: number;
    earliestDate: string;
    bookingType: ExperienceBookingType;
    closures: ExperienceClosuresType;
    optionalServices: ExperienceOptionalServiceType[];
  },
): string | null {
  if (!draft.experienceId) return 'No experience was named.';
  if (!draft.customerFirstName.trim() || !draft.customerLastName.trim()) {
    return 'Please give the guest’s first and last name.';
  }
  if (
    draft.customerFirstName.length > bookingRequestMaxNameLength
    || draft.customerLastName.length > bookingRequestMaxNameLength
  ) {
    return 'That name is longer than we can store.';
  }
  if (!draft.contactPhone.trim()) return 'Please give a contact phone number.';
  if (draft.contactPhone.length > bookingRequestMaxPhoneLength) {
    return 'That phone number is longer than we can store.';
  }

  if (isCalendarBookingType(limits.bookingType)) {
    if (!draft.slotId) return 'Please choose an available time.';
  } else {
    const dates = cleanPreferredDates(draft.preferredDates);
    if (dates.length === 0) return 'Please choose at least one preferred date.';
    if (dates.length > bookingRequestMaxDates) {
      return `Please offer no more than ${bookingRequestMaxDates} dates.`;
    }
    if (dates.some((d) => !isoDatePattern.test(d))) return 'One of the dates could not be read.';
    if (dates.some((d) => d < limits.earliestDate)) return 'Please choose dates in the future.';
    // Named in the message rather than reported as "one of your dates": the
    // concierge has up to five in front of them, and the one to change is the
    // whole of what they need to know. The reason comes with it for the same
    // reason it is collected at all — "away, family holiday" tells them whether
    // to try the following week or a different experience.
    for (const date of dates) {
      const closed = closureMessageFor(date, limits.closures);
      if (closed) return `${closed} Please offer another date.`;
    }
  }

  if (!Number.isInteger(draft.guests)) return 'Please choose the number of guests.';
  if (draft.guests < limits.minGuests || draft.guests > limits.maxGuests) {
    return `This experience takes ${limits.minGuests}–${limits.maxGuests} guests.`;
  }

  // A tick on a service the experience no longer offers. Refused rather than
  // dropped: the concierge asked for a car, and a booking that quietly arrives
  // without one is worse than being told to look at the list again.
  const { unknown } = resolveOptionalServices(limits.optionalServices, cleanOptionalServiceIds(draft.optionalServiceIds));
  if (unknown.length > 0) {
    return 'One of the additional services you chose is no longer offered for this experience. '
      + 'Please reload the page and choose again.';
  }

  if (draft.specialRequests.length > bookingRequestMaxRequestsLength) {
    return 'Please shorten the special requests a little.';
  }

  return null;
}

// --- Callables ---

export interface CreateBookingRequestResponseType {
  requestId: string;
  /**
   * The request code, where one was minted — what the confirmation screen quotes
   * back, and what every email about this booking will carry.
   *
   * Absent where the booking could not be given one (an experience or partner
   * with no two-character code), and absent from a response by a function
   * deployed before codes existed. The screen falls back to the id, which is what
   * it showed before.
   */
  requestCode?: string;
  /** False when the notification email could not be sent. See `notifyError`. */
  notified: boolean;
  /**
   * Where it landed: 'pending' for an inquiry, 'confirmed' for a direct booking
   * off the calendar, which needs no decision from us.
   *
   * Returned rather than inferred from the experience, because the form must not
   * tell a concierge their booking is confirmed on the strength of what its own
   * copy of the experience said. Absent on a response from a function deployed
   * before direct booking existed; treat that as 'pending'.
   */
  status?: BookingRequestStatusType;
  /**
   * For a direct booking: whether the partner's written confirmation went out. The
   * booking stands either way — see `offerSent` on the transition response for
   * the same shape and the same reasoning.
   */
  confirmationSent?: boolean;
  /** The slot taken, for reading the booking back to the concierge. `HH:mm` JST. */
  confirmedDate?: string;
  confirmedStartTime?: string;
  confirmedEndTime?: string;
}

/**
 * What one transition needs, discriminated by where it is going.
 *
 * A union rather than one bag of optional fields, so that "held without a date"
 * or "cancelled without a reason" cannot be expressed — the compiler refuses
 * them on the way in, and the handler re-checks on the way through.
 *
 * 'expired' is absent deliberately: a hold lapsing is the scheduled task's to
 * decide, never a person's.
 */
export type SetBookingRequestStatusRequestType =
  | {
    requestId: string;
    status: 'held';
    /** Which of the preferred dates the supplier agreed to. */
    confirmedDate: string;
    /**
     * What the partner is being asked to agree to, itemised. At least one row.
     *
     * The rows rather than the total, because the total is derived from them and
     * a payload carrying both could disagree with itself — see `agreedPrice`.
     */
    priceLines: BookingPriceLineType[];
    /** ISO instant. When the offer lapses if unanswered. */
    heldUntil: string;
    /**
     * The note that goes to the partner with this offer, and the one that does
     * not. Both optional, and both rewritten by every offer — an empty string
     * clears whatever the last offer said rather than leaving it standing.
     *
     * They travel together and are stored apart: `publicNotes` onto the request,
     * `internalNotes` into `bookingRequestNotes`, where the partner cannot read it.
     */
    publicNotes?: string;
    internalNotes?: string;
  }
  | {
    requestId: string;
    status: 'confirmed';
    /**
     * Which date the supplier agreed to, and at what price. Both omitted when
     * confirming a hold — the request already carries them. Both required when
     * confirming straight from 'pending', where no hold ever recorded them.
     */
    confirmedDate?: string;
    priceLines?: BookingPriceLineType[];
  }
  | {
    requestId: string;
    status: 'cancelled';
    initiator: CancellationInitiatorType;
    /** Free text, in the admin's own words. Required, and never emailed to the partner. */
    reason: string;
    /** JPY. Ignored unless the initiator and the window make it chargeable. */
    cancellationFee?: number;
    /** Drops a chargeable fee to zero. Ignored where nothing was chargeable anyway. */
    waiveFee?: boolean;
  }
  | {
    requestId: string;
    status: 'withdrawn' | 'unavailable';
    reason: BookingRequestEndReasonType;
  };

/**
 * What a partner sends to cancel its own confirmed booking.
 *
 * `acceptedFee` is the figure the screen showed them, not a figure they get to
 * choose: the server computes what is actually due and refuses anything less. So a
 * page that sat open across the free-cancellation deadline cannot cancel on the
 * strength of the ¥0 it was displaying — the partner is told the real amount and
 * asked again. Sending more than is due is harmless; the amount written is always
 * the server's.
 *
 * Nothing else is in here on purpose. The reason and the initiator are what this
 * act is rather than something to state, and a payload that could name a reason
 * could name the one that is free of charge.
 */
export interface CancelOwnBookingRequestType {
  requestId: string;
  /** JPY. What the partner has been shown and accepted. */
  acceptedFee: number;
}

export interface CancelOwnBookingResponseType {
  /** The fee actually applied, in JPY. Zero when the cancellation was free. */
  cancellationFee: number;
}

/**
 * What a partner sends to answer its own held offer from the portal.
 *
 * The same two answers as the emailed link, and deliberately nothing else: the
 * date and the price are the offer's, not the caller's, so there is no field
 * here that could restate them. What differs from
 * `RespondToBookingOfferRequestType` is only the credential — an account at the
 * partner rather than a token — which is why this is a separate payload rather
 * than that one with the token made optional.
 */
export interface AnswerOwnBookingOfferRequestType {
  requestId: string;
  answer: 'confirm' | 'decline';
}

export interface AnswerOwnBookingOfferResponseType {
  /**
   * Where the request ended up: 'confirmed' or 'withdrawn'. Unlike the link
   * path this never comes back as 'expired' or 'already-answered' — the caller
   * is signed in and looking at a live list, so an offer that moved underneath
   * them is an error with a message rather than a state to render.
   */
  status: BookingRequestStatusType;
}

/**
 * What one booking's reference is being set to.
 *
 * An empty `reference` clears it, which is the whole reason this is a field edit
 * rather than an addition: a code keyed against the wrong booking has to come
 * off it, and until /portal raises the requests itself, keying them in by hand
 * is how they get on there in the first place.
 *
 * Its own callable rather than a field on the transition above, because it is
 * not a transition: it can be set at any point in a request's life, including
 * after it has ended, and nothing about the workflow turns on it.
 */
export interface SetBookingReferenceRequestType {
  requestId: string;
  /** Trimmed by the server. Empty removes the reference. */
  reference: string;
}

export interface SetBookingRequestStatusResponseType {
  status: BookingRequestStatusType;
  /** The fee actually applied, in JPY. Zero when the cancellation was free. */
  cancellationFee: number;
  /**
   * False when the booking has not reached /portal. Always false for now: the
   * endpoints do not exist yet, so a confirmation is left at
   * `portalSyncState: 'pending'` for the reconciliation to pick up.
   */
  portalSynced: boolean;
  /** False when the offer email could not be sent. See `offerEmailError`. */
  offerSent?: boolean;
}

// --- The offer link ---

/**
 * Where an offer email points. `?t=` rather than a path segment to match
 * `/invite/:inviteId?t=…`, and because a query string is the half of a URL that
 * referrer headers are most consistently stripped of.
 */
export function bookingOfferPath(requestId: string, token: string): string {
  return `/booking/${requestId}?t=${encodeURIComponent(token)}`;
}

/**
 * Whether an offer link can still be acted on, and if not, why.
 *
 * 'not-found' covers a wrong token as well as a missing request: a visitor
 * holding the id but not the secret learns nothing either way.
 */
export type BookingOfferCheckResultType =
  | 'ok'
  | 'not-found'
  | 'expired'
  | 'already-answered';

export const bookingOfferCheckMessages: Record<Exclude<BookingOfferCheckResultType, 'ok'>, string> = {
  'not-found': 'This link is not valid. It may have been mistyped, or replaced by a newer offer for the same request.',
  expired: 'This offer has expired. Please contact Eighty Days if you would still like the date.',
  'already-answered': 'This offer has already been answered. Please contact Eighty Days if you need to change it.',
};

/**
 * What the offer page shows before anybody has clicked anything.
 *
 * Deliberately narrow. The link is unauthenticated, so everything here is what
 * we accept a forwarded email disclosing: the experience, the date, the price
 * and the deadline. Not the guest's name, not the partner's contact details.
 */
export interface BookingOfferPreviewType {
  result: BookingOfferCheckResultType;
  experienceName?: string;
  partnerName?: string;
  /**
   * What to quote when ringing us about this offer — see `bookingReference`.
   *
   * Safe to send to whoever holds the link, because the offer email already
   * carried it: this page shows what that letter said, and nothing more.
   */
  reference?: string;
  /** `yyyy-mm-dd`. The date the supplier agreed to hold. */
  confirmedDate?: string;
  /** JPY. The total. */
  agreedPrice?: number;
  /**
   * And what makes it up, where the offer was itemised. Disclosed for the same
   * reason the total is: the offer email already listed these rows, and this page
   * is the partner's copy of that letter.
   */
  priceLines?: BookingPriceLineType[];
  guests?: number;
  /** ISO instant. When the offer lapses if unanswered. */
  heldUntil?: string;
  /**
   * The cancellation terms being offered, resolved server-side. Sent as the
   * answer rather than as the policy behind it: the browser is shown what it may
   * state, and never left to work out a term of business for itself.
   */
  cancellation?: OfferCancellationType;
  /**
   * The desk's note to the partner, where the offer carries one. Safe on an
   * unauthenticated page for the same reason the price is: it was written to be
   * sent to this partner, and the offer email already said it.
   */
  publicNotes?: string;
  /** Set once answered, so the page can say what the answer was. */
  status?: BookingRequestStatusType;
}

export interface RespondToBookingOfferRequestType {
  requestId: string;
  token: string;
  answer: 'confirm' | 'decline';
}

export interface RespondToBookingOfferResponseType {
  result: BookingOfferCheckResultType;
  /** Where the request ended up. Absent unless `result` is 'ok'. */
  status?: BookingRequestStatusType;
}
